import { AlertTriangle, LockKeyhole } from "lucide-react";
import { useState, type ReactNode, type RefObject } from "react";
import { Sheet } from "../../components/Sheet";
import { Button } from "../../components/primitives";
import { googleAllowedDomain } from "../../lib/env";

/**
 * THE DOOR AND THE DOCUMENT.
 *
 * Everyone who reaches this URL is already a Meridian employee (`hd` goes to GIS and
 * handleCredentialResponse rejects anything else), so there is nobody to sell to — this is not
 * a landing page and it does not get a brochure headline, proof cards, or a pseudo-document
 * drawn for a user who has no document. But there IS one thing worth showing: the thing they
 * are about to compile.
 *
 * So the screen is a DOOR (brand, h1, one action, the domain rule, recovery, the three
 * governance rules) standing beside a STAGE holding a rendered SPECIMEN of the canonical ATLAS
 * DPI Concept Note, with the protected section visibly sealed. The product's whole promise —
 * protected facts are never rewritten — is not asserted in copy here, it is DEMONSTRATED, in
 * serif, before you have signed in.
 *
 * An earlier pass forbade itself amber, gradient, colour and any card, and centred ~320px of
 * content in a 100dvh box. The result was a 400px ribbon of grey text stranded in a 1440px grey
 * field: correct, honest, and lifeless. Those self-imposed rules are RETIRED and replaced with
 * reasoned ones: amber appears exactly ONCE, as the PROTECTED seal inside the specimen (amber's
 * reserved system meaning, used correctly), the governance list keeps its neutral --muted lock,
 * and from 1024 up the page is a door plus a stage — so neither column can be empty. The split
 * is at 1024 and NOT at 768 because a 400px door beside a 368px stage starves both: on iPad
 * portrait the phone's three-band composition is the better one, and it is what runs there.
 *
 * HONESTY OF THE SPECIMEN. It states no funder, no run, no progress, no result, no endorsement
 * and NO PAGE COUNT (the app holds no page-count constant; do not type one). Its four section
 * titles AND their prose are the app's own source of record — createDocument() / buildEditPlan()
 * in src/lib/run.ts — and the sealed one is the section that is `locked: true` there. Nothing on
 * this screen implies a check has passed or that any work exists yet.
 *
 * FROZEN: `.google-button-slot` is rendered into by the Google Identity script, which computes
 * the button width from the div's getBoundingClientRect() ONCE, synchronously, and clamps it to
 * [260, 360]. The slot measures 288px at 320, 358px at 390, --gis-w 360 in the tablet band, and
 * 352px inside the 400px door column at >=1024 — every one of them above the 260 floor and at or
 * under the 360 cap. Never give the slot display:none/contents, visibility:hidden, a
 * zero-width parent, a clipped parent, or a TRANSFORMED ancestor (a transform changes
 * getBoundingClientRect). The one animation on this screen is on the SPECIMEN, which is not an
 * ancestor of the slot. authRequired is false in dev, so every mistake here is invisible locally
 * and fatal in production.
 */

const GOVERNANCE_RULES = [
  {
    title: "Protected facts are never rewritten",
    body: "Numbers, dates, currencies, citations, URLs and defined terms in the ATLAS source are locked. The compiler proposes edits around them."
  },
  {
    title: "Every proposed edit is decided by a person",
    body: "No text change reaches the document until you accept, reject or rewrite it."
  },
  {
    title: "Final QA runs before a package is exported",
    body: "Six checks: protected facts, editable blocks, research rigor, patch application, rendering, reviewers. A check that has not run is reported as not run, never as passed."
  }
];

/**
 * THE SPECIMEN'S TEXT IS READ FROM THE SOURCE OF RECORD, NOT INVENTED HERE.
 *
 * Round 1 printed the four section names and nothing else, and it read as a settings menu
 * typeset in serif rather than as the canonical Concept Note. A document product's front door
 * has to show a DOCUMENT: prose, hierarchy, a sealed passage. So each of the four sections
 * carries a real line of the note.
 *
 * EVERY SENTENCE BELOW IS THE APP'S OWN. The four titles, the `locked: true` flag on
 * "Protected position", and the prose are createDocument() and buildEditPlan() in
 * src/lib/run.ts — the compiler's source of record — reproduced, not embellished:
 *
 *   "ATLAS is presented as a sovereign medical AI evaluation initiative."   run.ts (opening fallback)
 *   "...without changing the core proposal."                                run.ts (opening frame body)
 *   "...without implying endorsement, partnership, or that the request is secured."
 *                                                                           run.ts (audience fit body)
 *   "The funding request, phasing, country-node status, citations, and certainty language
 *    remain unchanged."                                                     run.ts (protected position, locked)
 *   "The closing asks for a focused exploratory conversation."              run.ts (next step)
 *
 * NOTHING IS ADDED. No funder (the personalization target is the literal placeholder
 * "[partner organization]", exactly as the walkthrough hero writes it — never a real name), no
 * run, no page count (the app holds no page-count constant; do not type one), no progress, no
 * result, no endorsement. The specimen depicts the source of record and asserts nothing about
 * work that has not happened.
 */
const SPECIMEN_SECTIONS: { title: string; sealed: boolean; body: ReactNode }[] = [
  {
    title: "Opening frame",
    sealed: false,
    body: (
      <>
        ATLAS is presented as a sovereign medical AI evaluation initiative. Each run reframes this opening in the
        language of <span className="spec-slot">[partner organization]</span> without changing the core proposal.
      </>
    )
  },
  {
    title: "Audience fit",
    sealed: false,
    body: "The note is made to speak to one reader's stated priorities, without implying endorsement, partnership, or that the request is secured.",
  },
  {
    title: "Protected position",
    sealed: true,
    body: "The funding request, phasing, country-node status, citations, and certainty language remain unchanged."
  },
  {
    title: "Next step",
    sealed: false,
    body: "The closing asks for a focused exploratory conversation."
  }
];

/**
 * GOVERNANCE RULES — a NEUTRAL-GLYPH variant of the RuleList primitive, local to this screen.
 *
 * WHY THIS IS NOT `<RuleList>`: the shared primitive hardcodes its lock glyph to `--amber-deep`
 * (primitives.css `.rule-list svg`). Amber is the master contract's CAUTION/PROTECTED colour, and
 * on THIS screen amber is spent once, deliberately, on the specimen's PROTECTED seal. Three amber
 * locks in a footer list would spend it on a promise rather than on a protected fact and would
 * make the one meaningful amber event on the page invisible. So this list's lock is --muted.
 *
 * It REUSES the `.rule-list` class so it inherits the primitive's exact layout and typography, and
 * adds two token-only overrides SCOPED to this instance (`.auth-rules svg { color: var(--muted) }`
 * and `.auth-rules li { padding-inline: 0 }`, the latter so the rows drop the primitive's own
 * --panel-pad and land on the SAME left edge as the h1, the lede and the Google button).
 *
 * FLAGGED FOR CONSOLIDATION: fold this into RuleList as a `tone` prop (default protected/amber,
 * `neutral` = muted) so this local copy can be deleted.
 */
function GovernanceRules() {
  return (
    <ul className="rule-list auth-rules">
      {GOVERNANCE_RULES.map((rule) => (
        <li key={rule.title}>
          <LockKeyhole aria-hidden="true" />
          <span>
            <strong>{rule.title}</strong>
            <small>{rule.body}</small>
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * THE SPECIMEN. A depiction of the source of record — inert, no interactive elements, no tab
 * stops. It is NOT aria-hidden: it carries meaning (this is what you are about to work on), so
 * the figure is named by an sr-only <figcaption> instead. The two ghost sheets behind it are real
 * sibling divs rather than pseudo-elements, so nothing ever paints over the page's own edge, and
 * they are inset INSIDE the frame's padding, so the stack cannot produce horizontal overflow.
 *
 * IT IS A PAGE, NOT A LIST. Round 1 rendered the four section names as rows with hairlines
 * between them, and it read as a menu: a document product whose front door showed no prose. Now
 * each section carries a real line of the note in Source Serif 4, the personalization target is
 * shown as the literal placeholder it actually is, and the protected section is SEALED in place —
 * so the product's central promise is demonstrated in type before anyone has signed in, rather
 * than asserted in marketing copy.
 */
function CanonicalSpecimen() {
  return (
    <figure className="auth-specimen-frame">
      <div className="spec-sheet spec-sheet-far" aria-hidden="true" />
      <div className="spec-sheet spec-sheet-near" aria-hidden="true" />

      <div className="auth-specimen">
        <div className="spec-head">
          <p className="spec-kicker">Canonical source</p>
          <p className="spec-title">ATLAS DPI Concept Note</p>
          <hr className="spec-rule" />
        </div>

        <div className="spec-body">
          {SPECIMEN_SECTIONS.map((section) =>
            section.sealed ? (
              /* THE SEALED PASSAGE. The one saturated colour event on this screen, and it is
                 amber's reserved system meaning (PROTECTED) spent on the one protected thing
                 here. A FULL border on all four sides — never a rail down one edge — a glyph
                 AND the word, never colour alone. */
              <section className="spec-section spec-section-sealed" key={section.title}>
                <div className="spec-section-head">
                  <p className="spec-section-title">{section.title}</p>
                  <span className="spec-seal">
                    <LockKeyhole aria-hidden="true" />
                    Sealed
                  </span>
                </div>
                <p className="spec-text">{section.body}</p>
              </section>
            ) : (
              <section className="spec-section" key={section.title}>
                <div className="spec-section-head">
                  <p className="spec-section-title">{section.title}</p>
                </div>
                <p className="spec-text">{section.body}</p>
              </section>
            )
          )}
        </div>
      </div>

      <figcaption className="sr-only">
        Specimen of the canonical ATLAS DPI Concept Note. Its protected section is sealed and is never rewritten.
      </figcaption>
    </figure>
  );
}

export function SignInScreen({ buttonRef, error }: { buttonRef: RefObject<HTMLDivElement>; error: string }) {
  const [helpOpen, setHelpOpen] = useState(false);

  return (
    <main className="auth-screen" data-testid="signin-page">
      {/* The brand, stated ONCE. It was rendered twice — here and again inside the sign-in card.
          It is a <div>, not an <a href="/">: that link reloaded the page you are already on. */}
      <div className="auth-topbar">
        <div className="auth-brand">
          <img src="/meridian-logo.svg" alt="" />
          <span className="auth-brand-name">
            ATLAS Compiler
            <small>by Meridian</small>
          </span>
        </div>
      </div>

      {/* No 100dvh centring on the phone — that trick was the origin of every void on this
          screen. The door is a band with real content above and below it, and the Google button
          lands in the thumb zone because the content puts it there. */}
      <section className="auth-door" aria-labelledby="auth-door-title">
        <div className="auth-door-head">
          <h1 id="auth-door-title">Sign in</h1>
          <p className="auth-lede">Personalize the ATLAS DPI concept note for a named funder or partner.</p>
        </div>

        <div className="auth-slot-block">
          {/* FROZEN — see the header of this file and of the CSS layer. */}
          <div ref={buttonRef} className="google-button-slot" />

          {/* The one governed choice on this screen is WHICH ACCOUNT, and its consequence is the
              domain restriction. So the consequence sits WITH the control it governs, instead of
              buried in card copy. */}
          <p className="auth-domain" data-testid="signin-domain">
            <LockKeyhole aria-hidden="true" />
            <span>
              Meridian Google accounts only. Use your @{googleAllowedDomain} address.
            </span>
          </p>

          {/* THE ERROR IS HEARD. This region is ALWAYS in the DOM (idle it is an empty flex box
              with no padding and no border, so it has zero height and opens no gap — the gate
              asserts offsetHeight === 0) and the message is injected into it: glyph + words +
              colour, never colour alone, and a FULL border on all four sides, never one edge. */}
          <div
            className="auth-error-slot"
            role="alert"
            aria-live="assertive"
            data-testid="signin-error"
            data-state={error ? "error" : "idle"}
          >
            {error ? (
              <>
                <AlertTriangle aria-hidden="true" />
                <span>{error}</span>
              </>
            ) : null}
          </div>
        </div>

        {/* RECOVERY ON DEMAND, ON EVERY VIEWPORT. This replaces `.mobile-auth-note`, a permanent
            pre-emptive worry ("On iPhone, open this page in Safari or Chrome…") that was
            display:none above 768px — so a desktop user blocked by a popup blocker got no
            guidance at all, while a phone user was told to worry before anything had gone wrong. */}
        <Button
          variant="ghost"
          size="sm"
          className="auth-help"
          data-testid="signin-help"
          onClick={() => setHelpOpen(true)}
        >
          Trouble signing in?
        </Button>
      </section>

      {/* THE STAGE. On the phone it is the middle band (white door / tinted stage / white notes),
          so the phone gets rhythm and colour too; from 768 up it is the right pane and the door
          column is full top-to-bottom, which is what makes a dead column structurally impossible. */}
      <div className="auth-stage">
        <CanonicalSpecimen />
      </div>

      {/* RuleList, NOT DataList: none of this is live state and it must never be able to read as
          one. That split is a trust fix — a product promise typeset like a verified check is
          exactly the lie this product exists to prevent. At >=768 this is the foot of the door
          column, above a --line hairline; it is never an orphan below the fold. */}
      <section className="auth-notes" aria-labelledby="auth-notes-title">
        <h2 id="auth-notes-title">How this tool is governed</h2>
        {/* Neutral-glyph RuleList variant — see GovernanceRules above. No aria-label on the list:
            the visible <h2> already names this region via aria-labelledby, and repeating the
            string would announce it twice. */}
        <GovernanceRules />
      </section>

      {helpOpen ? (
        <Sheet
          title="Trouble signing in"
          testId="signin-help-sheet"
          labelledBy="signin-help-title"
          onClose={() => setHelpOpen(false)}
          footer={
            <Button variant="secondary" onClick={() => setHelpOpen(false)}>
              Close
            </Button>
          }
        >
          <div className="auth-help-body">
            <p>
              Sign-in requires a Google account on the {googleAllowedDomain} domain. Personal Google accounts are
              declined.
            </p>
            <p>
              If Google opens a blank or error page, open this page in Safari or Chrome rather than inside another
              app&apos;s browser.
            </p>
            <p>
              If the Google button does not appear, your network may be blocking accounts.google.com. Reload the page.
            </p>
          </div>
        </Sheet>
      ) : null}
    </main>
  );
}
